import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import MatterDocumentsPanel from '../../components/MatterDocumentsPanel';
import ProfessionalLearningInsights from '../../components/education/ProfessionalLearningInsights';
import ProfessionalAssignLearning from '../../components/education/ProfessionalAssignLearning';
import * as FiIcons from 'react-icons/fi';
import { format } from 'date-fns';

const stringifyValue = (value) => {
  if (value === undefined || value === null || value === '') return 'Not provided';
  if (Array.isArray(value)) {
    if (value.length === 0) return 'Not provided';
    return value
      .map((item) => {
        if (item && typeof item === 'object') {
          return Object.values(item).filter(Boolean).join(' - ');
        }
        return String(item);
      })
      .filter(Boolean)
      .join('\n');
  }
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
};

const MatterDetail = () => {
  const { matterId } = useParams();
  const { profile } = useAuth();
  const navigate = useNavigate();

  const [matter, setMatter] = useState(null);
  const [intakeProgress, setIntakeProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startingIntake, setStartingIntake] = useState(false);
  const [invitingClient, setInvitingClient] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [inviteCopied, setInviteCopied] = useState(false);
  const [lawFirms, setLawFirms] = useState([]);
  const [lawFirmUsers, setLawFirmUsers] = useState([]);
  const [matterTasks, setMatterTasks] = useState([]);
  const [informationRequests, setInformationRequests] = useState([]);
  const [fieldOverrides, setFieldOverrides] = useState([]);
  const [requestResponses, setRequestResponses] = useState({});
  const [savingRequestId, setSavingRequestId] = useState(null);
  const [assignmentData, setAssignmentData] = useState({
    law_firm_id: '',
    assigned_attorney_id: ''
  });
  const [assigningMatter, setAssigningMatter] = useState(false);
  const [assignmentNotice, setAssignmentNotice] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMatterDetail = async () => {
      if (!profile?.advisor_firm_id || !matterId) return;

      try {
        setLoading(true);
        setError(null);

        // 1. Fetch matter with client join
        const { data, error: fetchErr } = await supabase
          .from('matters')
          .select(`
            *,
            client:clients (*)
          `)
          .eq('id', matterId)
          .eq('advisor_firm_id', profile.advisor_firm_id)
          .single();

        if (fetchErr) {
          console.error('MatterDetail fetch error:', fetchErr);
          throw fetchErr;
        }

        if (!data) {
          throw new Error("Plan not found or access denied.");
        }

        setMatter(data);
        setAssignmentData({
          law_firm_id: data.law_firm_id || '',
          assigned_attorney_id: data.assigned_attorney_id || ''
        });

        // 2. Fetch Intake Progress
        const { data: progressData } = await supabase
          .from('matter_intake_progress')
          .select('*')
          .eq('matter_id', matterId)
          .single();
        
        setIntakeProgress(progressData);

        // 3. Fetch law firm assignment options
        const [lawFirmRes, lawUserRes, taskRes, requestRes, overrideRes] = await Promise.all([
          supabase
            .from('law_firms')
            .select('id, firm_name, states_served')
            .order('firm_name', { ascending: true }),
          supabase
            .from('profiles')
            .select('id, first_name, last_name, email, role, law_firm_id')
            .in('role', ['attorney', 'legal_staff'])
            .order('last_name', { ascending: true })
          ,
          supabase
            .from('matter_tasks')
            .select('*')
            .eq('matter_id', matterId)
            .order('created_at', { ascending: false })
          ,
          supabase
            .from('matter_information_requests')
            .select('*')
            .eq('matter_id', matterId)
            .order('created_at', { ascending: false })
          ,
          supabase
            .from('matter_field_overrides')
            .select('*')
            .eq('matter_id', matterId)
            .order('created_at', { ascending: false })
        ]);

        if (lawFirmRes.error) {
          console.warn('Law firm assignment options error:', lawFirmRes.error);
        } else {
          setLawFirms(lawFirmRes.data || []);
        }

        if (lawUserRes.error) {
          console.warn('Law firm user assignment options error:', lawUserRes.error);
        } else {
          setLawFirmUsers(lawUserRes.data || []);
        }

        if (taskRes.error) {
          console.warn('Matter task visibility error:', taskRes.error);
        } else {
          setMatterTasks(taskRes.data || []);
        }

        if (requestRes.error) {
          console.warn('Matter information request visibility error:', requestRes.error);
        } else {
          const requests = requestRes.data || [];
          setInformationRequests(requests);
          setRequestResponses(
            requests.reduce((responses, request) => {
              responses[request.id] = request.response || '';
              return responses;
            }, {})
          );
        }

        if (overrideRes.error) {
          console.warn('Matter field override visibility error:', overrideRes.error);
        } else {
          setFieldOverrides(overrideRes.data || []);
        }

      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchMatterDetail();
  }, [matterId, profile]);

  const handleStartIntake = async () => {
    if (!matter || startingIntake) return;
    
    try {
      setStartingIntake(true);
      setError(null);

      const { data: questionnaires } = await supabase
        .from('intake_questionnaires')
        .select('id')
        .eq('status', 'published')
        .order('published_at', { ascending: false })
        .limit(1);

      const questionnaireId = questionnaires?.[0]?.id || null;

      // 1. Load active sections to get the first one
      let sectionsQuery = supabase
        .from('intake_sections')
        .select('id')
        .eq('is_active', true)
        .order('sort_order', { ascending: true })
        .limit(1);

      if (questionnaireId) {
        sectionsQuery = sectionsQuery.eq('questionnaire_id', questionnaireId);
      }

      const { data: sections, error: sectionsErr } = await sectionsQuery;

      if (sectionsErr) throw sectionsErr;
      if (!sections || sections.length === 0) {
        throw new Error("No intake sections are configured yet.");
      }

      const firstSectionId = sections[0].id;

      // 2. Upsert Progress
      const { error: upsertErr } = await supabase
        .from('matter_intake_progress')
        .upsert({
          matter_id: matter.id,
          current_section_id: firstSectionId,
          questionnaire_id: questionnaireId,
          status: 'in_progress',
          completion_percent: 0,
          started_at: new Date().toISOString()
        }, { onConflict: 'matter_id' });

      if (upsertErr) throw upsertErr;

      // 3. Update Matter Status
      const { error: statusErr } = await supabase
        .from('matters')
        .update({ status: 'intake_in_progress' })
        .eq('id', matter.id);

      if (statusErr) throw statusErr;

      // 4. Navigate
      navigate(`/matters/${matter.id}/intake`);

    } catch (err) {
      console.error('Start Intake Error:', err);
      setError(err.message);
      setStartingIntake(false);
    }
  };

  const copyInviteLink = async (link) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
        setInviteCopied(true);
      }
    } catch (err) {
      console.warn('Unable to copy invite link:', err);
    }
  };

  const handleShareWithClient = async () => {
    if (!matter || invitingClient) return;

    try {
      setInvitingClient(true);
      setInviteCopied(false);
      setError(null);

      const { data, error: inviteErr } = await supabase.rpc('create_client_invitation', {
        p_client_id: matter.client_id,
        p_matter_id: matter.id
      });

      if (inviteErr) throw inviteErr;

      const invitation = Array.isArray(data) ? data[0] : data;
      if (!invitation?.invite_path) {
        throw new Error('Invitation was created, but no invite path was returned.');
      }

      const link = `${window.location.origin}${invitation.invite_path}`;
      setInviteLink(link);
      await copyInviteLink(link);
    } catch (err) {
      console.error('Create client invitation error:', err);
      setError(err.message || 'Unable to create client invitation.');
    } finally {
      setInvitingClient(false);
    }
  };

  const handleAssignmentChange = (e) => {
    const { name, value } = e.target;

    setAssignmentData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'law_firm_id' ? { assigned_attorney_id: '' } : {})
    }));
  };

  const handleAssignMatter = async (e) => {
    e.preventDefault();
    if (!matter || assigningMatter) return;

    try {
      setAssigningMatter(true);
      setAssignmentNotice(null);
      setError(null);

      if (!assignmentData.law_firm_id) {
        throw new Error('Choose a law firm before assigning this plan.');
      }

      const { data, error: updateErr } = await supabase.rpc('assign_matter_to_law_firm', {
        p_matter_id: matter.id,
        p_law_firm_id: assignmentData.law_firm_id,
        p_assigned_attorney_id: assignmentData.assigned_attorney_id || null
      });

      if (updateErr) throw updateErr;

      const assignedMatter = Array.isArray(data) ? data[0] : data;
      setMatter((current) => ({
        ...(current || {}),
        ...(assignedMatter || {}),
        client: current?.client
      }));
      setAssignmentData({
        law_firm_id: assignedMatter?.law_firm_id || '',
        assigned_attorney_id: assignedMatter?.assigned_attorney_id || ''
      });
      setAssignmentNotice('Plan assignment updated.');
    } catch (err) {
      console.error('Matter assignment error:', err);
      setError(err.message || 'Unable to assign plan.');
    } finally {
      setAssigningMatter(false);
    }
  };

  const handleRequestResponseChange = (requestId, value) => {
    setRequestResponses((prev) => ({
      ...prev,
      [requestId]: value
    }));
  };

  const handleSubmitInformationResponse = async (request) => {
    const response = requestResponses[request.id]?.trim();
    if (!response || savingRequestId) return;

    try {
      setSavingRequestId(request.id);
      setError(null);

      const { data, error: updateErr } = await supabase
        .from('matter_information_requests')
        .update({
          response,
          status: 'answered',
          answered_at: new Date().toISOString()
        })
        .eq('id', request.id)
        .select('*')
        .single();

      if (updateErr) throw updateErr;

      setInformationRequests((current) => current.map((item) => (item.id === data.id ? data : item)));
      setRequestResponses((prev) => ({ ...prev, [data.id]: data.response || '' }));
    } catch (err) {
      console.error('Information request response error:', err);
      setError(err.message || 'Unable to submit information response.');
    } finally {
      setSavingRequestId(null);
    }
  };

  const getStatusBadge = (value, type) => {
    const baseClasses = "px-2.5 py-0.5 rounded-full text-xs font-medium border capitalize";
    
    if (type === 'status') {
      switch (value) {
        case 'matter_created': return <span className={`${baseClasses} bg-blue-50 text-blue-700 border-blue-100`}>Plan Created</span>;
        case 'intake_in_progress': return <span className={`${baseClasses} bg-amber-50 text-amber-700 border-amber-100`}>Intake In Progress</span>;
        case 'intake_submitted': return <span className={`${baseClasses} bg-green-50 text-green-700 border-green-100`}>Intake Submitted</span>;
        case 'ready_for_attorney_review': return <span className={`${baseClasses} bg-amber-50 text-amber-700 border-amber-100`}>Ready for Attorney Review</span>;
        case 'attorney_review': return <span className={`${baseClasses} bg-amber-50 text-amber-700 border-amber-100`}>Attorney Review In Progress</span>;
        case 'more_information_needed': return <span className={`${baseClasses} bg-amber-50 text-amber-700 border-amber-100`}>Waiting for Additional Information</span>;
        case 'attorney_review_complete': return <span className={`${baseClasses} bg-emerald-50 text-emerald-700 border-emerald-100`}>Attorney Review Complete</span>;
        case 'ready_for_document_generation': return <span className={`${baseClasses} bg-emerald-50 text-emerald-700 border-emerald-100`}>Ready for Document Generation</span>;
        case 'documents_generated': return <span className={`${baseClasses} bg-emerald-50 text-emerald-700 border-emerald-100`}>Documents Generated</span>;
        case 'completed': return <span className={`${baseClasses} bg-green-100 text-green-800 border-green-200`}>Completed</span>;
        default: return <span className={`${baseClasses} bg-slate-50 text-slate-700 border-slate-100`}>{value?.replace('_', ' ')}</span>;
      }
    }

    if (type === 'fee') {
      switch (value) {
        case 'not_started': return <span className={`${baseClasses} bg-slate-50 text-slate-600 border-slate-200`}>Fee: Not Started</span>;
        case 'paid': return <span className={`${baseClasses} bg-green-50 text-green-700 border-green-100`}>Fee: Paid</span>;
        default: return <span className={`${baseClasses} bg-amber-50 text-amber-700 border-amber-100`}>{value?.replace('_', ' ')}</span>;
      }
    }

    if (type === 'complexity') {
      return <span className={`${baseClasses} bg-slate-100 text-slate-600 border-slate-200`}>Complexity: {value}</span>;
    }

    return null;
  };

  const getIntakeButtonText = () => {
    if (!intakeProgress) return "Start Intake";
    if (intakeProgress.status === 'submitted') return "Review Intake";
    return "Continue Intake";
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <SafeIcon icon={FiIcons.FiLoader} className="animate-spin h-10 w-10 text-slate-300 mb-4" />
        <p className="text-slate-500">Loading plan details...</p>
      </div>
    );
  }

  if (error && !matter) {
    return (
      <div className="max-w-2xl mx-auto mt-12 p-8 bg-white rounded-xl shadow-sm border border-red-100 text-center">
        <SafeIcon icon={FiIcons.FiAlertCircle} className="h-12 w-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-slate-900">Unable to load plan</h2>
        <p className="text-slate-600 mt-2">{error}</p>
        <button 
          onClick={() => navigate('/matters')} 
          className="mt-6 inline-flex items-center px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50"
        >
          Back to all plans
        </button>
      </div>
    );
  }

  const PlaceholderCard = ({ title, icon }) => (
    <div className="bg-slate-50 border border-slate-200 border-dashed rounded-lg p-6 flex flex-col items-center justify-center text-center opacity-60">
      <SafeIcon icon={icon} className="h-6 w-6 text-slate-400 mb-2" />
      <h4 className="text-sm font-medium text-slate-600">{title}</h4>
      <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-wider font-bold">Coming in later sprint</p>
    </div>
  );

  const selectedLawFirmUsers = lawFirmUsers.filter((user) => user.law_firm_id === assignmentData.law_firm_id);
  const assignedAttorney = lawFirmUsers.find((user) => user.id === matter.assigned_attorney_id);
  const assignedLawFirm = lawFirms.find((firm) => firm.id === matter.law_firm_id);
  const openInformationRequests = informationRequests.filter((request) => ['open', 'answered'].includes(request.status));
  const getLawFirmUserName = (userId) => {
    const user = lawFirmUsers.find((item) => item.id === userId);
    return user ? `${user.first_name} ${user.last_name}` : 'Unassigned';
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8 pb-12">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center">
          <button 
            onClick={() => navigate('/matters')} 
            className="mr-4 p-2 rounded-full hover:bg-white transition-colors text-slate-400 hover:text-slate-600"
          >
            <SafeIcon icon={FiIcons.FiArrowLeft} className="h-6 w-6" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900">
                {matter.client?.first_name} {matter.client?.last_name}
              </h1>
              {getStatusBadge(matter.status, 'status')}
            </div>
            <p className="text-sm text-slate-500 mt-1 uppercase tracking-tight font-medium">
              {matter.plan_type} • Created {format(new Date(matter.created_at), 'MMMM d, yyyy')}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50">
            Edit Details
          </button>
          <button
            onClick={handleShareWithClient}
            disabled={invitingClient}
            className="px-4 py-2 text-sm font-medium text-white bg-slate-800 rounded-md hover:bg-slate-900 shadow-sm disabled:opacity-50"
          >
            {invitingClient ? 'Creating Invite...' : 'Share with Client'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md text-sm text-red-700">
          {error}
        </div>
      )}

      {inviteLink && (
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <h3 className="text-sm font-bold text-blue-900">Client invitation link created</h3>
              <p className="text-xs text-blue-700 mt-1">
                Send this secure link to {matter.client?.first_name} using your normal client communication channel.
              </p>
              <p className="mt-2 text-xs font-mono text-blue-900 break-all bg-white/70 rounded border border-blue-100 px-2 py-1">
                {inviteLink}
              </p>
            </div>
            <button
              onClick={() => copyInviteLink(inviteLink)}
              className="inline-flex items-center justify-center px-4 py-2 text-xs font-bold uppercase tracking-widest rounded-md bg-white text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors"
            >
              <SafeIcon icon={inviteCopied ? FiIcons.FiCheck : FiIcons.FiCopy} className="mr-2 h-4 w-4" />
              {inviteCopied ? 'Copied' : 'Copy Link'}
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column: Core Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Intake Progress Section */}
          <div className="bg-white shadow-sm rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
              <h3 className="font-bold text-slate-800 flex items-center text-sm uppercase tracking-tight">
                <SafeIcon icon={FiIcons.FiClipboard} className="mr-2 h-4 w-4 text-slate-400" /> 
                Intake Questionnaire
              </h3>
              {intakeProgress && (
                <span className="text-xs font-bold text-blue-600 uppercase tracking-widest">
                  {intakeProgress.completion_percent}% Complete
                </span>
              )}
            </div>
            <div className="p-6">
              {!intakeProgress ? (
                <div className="text-center py-6">
                  <p className="text-sm text-slate-500 mb-6">The intake process has not been started for this plan yet.</p>
                  <button 
                    onClick={handleStartIntake} 
                    disabled={startingIntake}
                    className="inline-flex items-center px-6 py-2 bg-slate-800 text-white text-sm font-medium rounded-md hover:bg-slate-900 disabled:opacity-50 transition-colors shadow-sm"
                  >
                    {startingIntake ? <SafeIcon icon={FiIcons.FiLoader} className="animate-spin mr-2 h-4 w-4" /> : <SafeIcon icon={FiIcons.FiPlay} className="mr-2 h-4 w-4" />}
                    Start Intake Process
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Current Status</label>
                      <p className="text-sm font-semibold text-slate-900 capitalize">
                        {intakeProgress.status?.replace('_', ' ')}
                      </p>
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Started On</label>
                      <p className="text-sm text-slate-600">{format(new Date(intakeProgress.started_at), 'MMM d, yyyy')}</p>
                    </div>
                    {intakeProgress.submitted_at && (
                      <div>
                        <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Submitted On</label>
                        <p className="text-sm text-slate-600">{format(new Date(intakeProgress.submitted_at), 'MMM d, yyyy')}</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-700" 
                      style={{ width: `${intakeProgress.completion_percent}%` }}
                    ></div>
                  </div>

                  <div className="flex flex-col sm:flex-row justify-end gap-3 pt-2">
                    <button 
                      onClick={() => navigate(`/matters/${matter.id}/intake-summary`)}
                      className="inline-flex items-center justify-center px-4 py-2 bg-white border border-slate-300 text-slate-700 text-xs font-bold uppercase tracking-widest rounded-md hover:bg-slate-50 transition-colors"
                    >
                      <SafeIcon icon={FiIcons.FiEye} className="mr-2 h-3.5 w-3.5" /> View Summary
                    </button>
                    <button 
                      onClick={() => navigate(`/matters/${matter.id}/intake`)}
                      className="inline-flex items-center justify-center px-4 py-2 bg-slate-100 text-slate-700 text-xs font-bold uppercase tracking-widest rounded-md hover:bg-slate-200 transition-colors"
                    >
                      {getIntakeButtonText()} <SafeIcon icon={FiIcons.FiChevronRight} className="ml-2 h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Matter Summary Card */}
          <ProfessionalLearningInsights matter={matter} />

          <ProfessionalAssignLearning matter={matter} profile={profile} />

          {/* Matter Summary Card */}
          {informationRequests.length > 0 && (
            <div className="bg-white shadow-sm rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 bg-amber-50/70 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <h3 className="font-bold text-amber-950 flex items-center text-sm uppercase tracking-tight">
                  <SafeIcon icon={FiIcons.FiMessageSquare} className="mr-2 h-4 w-4 text-amber-600" />
                  Attorney Information Requests
                </h3>
                <span className="text-xs font-bold uppercase tracking-widest text-amber-700">
                  {openInformationRequests.length} Open
                </span>
              </div>
              <div className="divide-y divide-slate-100">
                {informationRequests.map((request) => (
                  <div key={request.id} className="p-6 space-y-4">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{request.question}</p>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mt-1">
                          {request.status.replaceAll('_', ' ')}
                          {request.created_at && ` - Requested ${format(new Date(request.created_at), 'MMM d, yyyy')}`}
                        </p>
                      </div>
                      <span className={`px-2.5 py-0.5 inline-flex text-xs leading-4 font-semibold rounded-full border capitalize ${
                        request.status === 'resolved'
                          ? 'bg-green-50 text-green-700 border-green-100'
                          : request.status === 'answered'
                            ? 'bg-blue-50 text-blue-700 border-blue-100'
                            : request.status === 'cancelled'
                              ? 'bg-slate-100 text-slate-500 border-slate-200'
                              : 'bg-amber-50 text-amber-700 border-amber-100'
                      }`}>
                        {request.status.replaceAll('_', ' ')}
                      </span>
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Advisor Response</label>
                      <textarea
                        rows={4}
                        value={requestResponses[request.id] || ''}
                        onChange={(event) => handleRequestResponseChange(request.id, event.target.value)}
                        disabled={!['open', 'answered'].includes(request.status) || savingRequestId === request.id}
                        className="block w-full sm:text-sm border-slate-300 rounded-md py-2 px-3 border focus:ring-slate-500 focus:border-slate-500 outline-none disabled:bg-slate-50"
                        placeholder="Provide the requested clarification for the attorney..."
                      />
                    </div>

                    {['open', 'answered'].includes(request.status) && (
                      <div className="flex justify-end">
                        <button
                          onClick={() => handleSubmitInformationResponse(request)}
                          disabled={!requestResponses[request.id]?.trim() || savingRequestId === request.id}
                          className="inline-flex items-center justify-center px-4 py-2 text-xs font-bold uppercase tracking-widest rounded-md text-white bg-slate-800 hover:bg-slate-900 disabled:opacity-50"
                        >
                          <SafeIcon icon={savingRequestId === request.id ? FiIcons.FiLoader : FiIcons.FiSend} className={`mr-2 h-4 w-4 ${savingRequestId === request.id ? 'animate-spin' : ''}`} />
                          {request.status === 'answered' ? 'Update Response' : 'Send Response'}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {fieldOverrides.length > 0 && (
            <div className="bg-white shadow-sm rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 bg-emerald-50/70 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <h3 className="font-bold text-emerald-950 flex items-center text-sm uppercase tracking-tight">
                  <SafeIcon icon={FiIcons.FiEdit3} className="mr-2 h-4 w-4 text-emerald-700" />
                  Attorney-Approved Values
                </h3>
                <span className="text-xs font-bold uppercase tracking-widest text-emerald-700">
                  {fieldOverrides.length} Saved
                </span>
              </div>
              <div className="divide-y divide-slate-100">
                {fieldOverrides.map((override) => (
                  <div key={override.id} className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                      <div className="min-w-0">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">{override.field_key}</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Original</p>
                            <p className="text-sm text-slate-700 whitespace-pre-line">{stringifyValue(override.source_value_json)}</p>
                          </div>
                          <div className="rounded-md border border-emerald-100 bg-emerald-50 p-3">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 mb-1">Approved</p>
                            <p className="text-sm text-emerald-950 whitespace-pre-line">{stringifyValue(override.approved_value_json)}</p>
                          </div>
                        </div>
                      </div>
                      {override.approved_at && (
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 whitespace-nowrap">
                          {format(new Date(override.approved_at), 'MMM d, yyyy')}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Matter Summary Card */}
          <div className="bg-white shadow-sm rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50">
              <h3 className="font-bold text-slate-800 text-sm uppercase tracking-tight">Plan Overview</h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Plan Type</label>
                  <p className="text-sm text-slate-900 capitalize font-medium">{matter.matter_type?.replace('_', ' ')}</p>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">State of Governance</label>
                  <p className="text-sm text-slate-900 font-medium">{matter.client_state || 'Not set'}</p>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Legal Fee Status</label>
                  <div className="mt-1">{getStatusBadge(matter.legal_fee_status, 'fee')}</div>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Complexity</label>
                  <div className="mt-1">{getStatusBadge(matter.complexity_level, 'complexity')}</div>
                </div>
              </div>
              
              <div className="mt-8">
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Advisor Notes</label>
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-100 text-sm text-slate-700 italic">
                  {matter.advisor_notes || "No internal notes provided for this plan."}
                </div>
              </div>
            </div>
          </div>

          <MatterDocumentsPanel matter={matter} mode="advisor" />

          <div className="bg-white shadow-sm rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50">
              <h3 className="font-bold text-slate-800 flex items-center text-sm uppercase tracking-tight">
                <SafeIcon icon={FiIcons.FiUserCheck} className="mr-2 h-4 w-4 text-slate-400" />
                Attorney Assignment
              </h3>
            </div>
            <form onSubmit={handleAssignMatter} className="p-6 space-y-5">
              {assignmentNotice && (
                <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded-md text-sm text-green-700">
                  {assignmentNotice}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Law Firm</label>
                  <select
                    name="law_firm_id"
                    value={assignmentData.law_firm_id}
                    onChange={handleAssignmentChange}
                    className="block w-full sm:text-sm border-slate-300 rounded-md py-2 px-3 border focus:ring-slate-500 focus:border-slate-500 outline-none bg-white"
                  >
                    <option value="">Choose law firm</option>
                    {lawFirms.map((firm) => (
                      <option key={firm.id} value={firm.id}>
                        {firm.firm_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Attorney</label>
                  <select
                    name="assigned_attorney_id"
                    value={assignmentData.assigned_attorney_id}
                    onChange={handleAssignmentChange}
                    disabled={!assignmentData.law_firm_id}
                    className="block w-full sm:text-sm border-slate-300 rounded-md py-2 px-3 border focus:ring-slate-500 focus:border-slate-500 outline-none bg-white disabled:bg-slate-50"
                  >
                    <option value="">Unassigned</option>
                    {selectedLawFirmUsers.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.first_name} {user.last_name} ({user.role.replace('_', ' ')})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-slate-100 pt-4">
                <p className="text-xs text-slate-500">
                  Current: {assignedLawFirm?.firm_name || 'No law firm assigned'}
                  {assignedAttorney ? ` - ${assignedAttorney.first_name} ${assignedAttorney.last_name}` : ''}
                </p>
                <button
                  type="submit"
                  disabled={assigningMatter || !assignmentData.law_firm_id}
                  className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md text-white bg-slate-800 hover:bg-slate-900 disabled:opacity-50 transition-colors"
                >
                  {assigningMatter ? (
                    <>
                      <SafeIcon icon={FiIcons.FiLoader} className="animate-spin mr-2 h-4 w-4" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <SafeIcon icon={FiIcons.FiSave} className="mr-2 h-4 w-4" />
                      Save Assignment
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <PlaceholderCard title="Draft Review" icon={FiIcons.FiEdit3} />
          </div>

          <div className="bg-white shadow-sm rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50">
              <h3 className="font-bold text-slate-800 flex items-center text-sm uppercase tracking-tight">
                <SafeIcon icon={FiIcons.FiCheckSquare} className="mr-2 h-4 w-4 text-slate-400" />
                Law Firm Tasks
              </h3>
            </div>
            {matterTasks.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {matterTasks.map((task) => (
                  <div key={task.id} className="p-5">
                    <div className="flex flex-wrap items-center gap-2">
                      <h4 className="text-sm font-semibold text-slate-900">{task.title}</h4>
                      <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest rounded-full bg-blue-50 text-blue-700 border border-blue-100">
                        {task.status.replaceAll('_', ' ')}
                      </span>
                      <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                        {task.priority}
                      </span>
                    </div>
                    {task.description && (
                      <p className="text-sm text-slate-600 mt-2">{task.description}</p>
                    )}
                    <p className="text-xs text-slate-500 mt-2">
                      Assigned to {getLawFirmUserName(task.assigned_to)}
                      {task.due_date && ` - Due ${format(new Date(`${task.due_date}T00:00:00`), 'MMM d, yyyy')}`}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-slate-500">
                <SafeIcon icon={FiIcons.FiCheckSquare} className="h-10 w-10 text-slate-200 mx-auto mb-3" />
                <p className="text-sm">No law firm tasks have been created for this plan yet.</p>
              </div>
            )}
          </div>
        </div>

        {/* Right Column: Sidebar */}
        <div className="space-y-6">
          {/* Client Contact Profile */}
          <div className="bg-white shadow-sm rounded-xl border border-slate-200 p-6">
            <h3 className="font-bold text-slate-800 mb-6 flex items-center text-sm uppercase tracking-tight">
              <SafeIcon icon={FiIcons.FiUser} className="mr-2 h-4 w-4 text-slate-400" /> Client Profile
            </h3>
            <div className="space-y-4">
              <div className="flex items-start">
                <SafeIcon icon={FiIcons.FiMail} className="h-4 w-4 text-slate-400 mt-1 mr-3" />
                <div>
                  <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Email Address</p>
                  <p className="text-sm text-slate-900 break-all font-medium">{matter.client?.email}</p>
                </div>
              </div>
              <div className="flex items-start">
                <SafeIcon icon={FiIcons.FiPhone} className="h-4 w-4 text-slate-400 mt-1 mr-3" />
                <div>
                  <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Phone Number</p>
                  <p className="text-sm text-slate-900 font-medium">{matter.client?.phone || 'Not provided'}</p>
                </div>
              </div>
              <div className="flex items-start">
                <SafeIcon icon={FiIcons.FiMapPin} className="h-4 w-4 text-slate-400 mt-1 mr-3" />
                <div>
                  <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Residency</p>
                  <p className="text-sm text-slate-900 font-medium">{matter.client?.state_of_residence}</p>
                </div>
              </div>
            </div>
            <button 
              onClick={() => navigate(`/clients/${matter.client_id}`)}
              className="w-full mt-6 py-2 px-4 border border-slate-200 rounded-lg text-xs font-bold uppercase tracking-widest text-slate-600 hover:bg-slate-50 transition-colors"
            >
              View Client History
            </button>
          </div>

          {/* Secure Messaging Placeholder */}
          <div className="bg-slate-900 rounded-xl p-6 text-white overflow-hidden relative">
            <div className="relative z-10">
              <h3 className="font-semibold mb-2 flex items-center text-sm">
                <SafeIcon icon={FiIcons.FiMessageSquare} className="mr-2 h-4 w-4 text-blue-400" /> Secure Messaging
              </h3>
              <p className="text-slate-400 text-[11px] mb-4">Chat with the assigned attorney or client securely.</p>
              <div className="py-8 text-center border border-slate-700 border-dashed rounded-lg bg-slate-800/50">
                <p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">Module Locked</p>
              </div>
            </div>
            <SafeIcon icon={FiIcons.FiLock} className="absolute -bottom-4 -right-4 h-24 w-24 text-white/5 rotate-12" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MatterDetail;
